import React, { useMemo } from 'react';
import { InputNumber } from 'primereact/inputnumber';
import classes from '../../bidAnalysisTemplate.module.scss';

interface MASDirectGlobalOutputTableProps {
  formData?: any;
  packSizeFormData?: Record<number, any>;
  activePackSizeTab?: number | null;
  bidApprovedBy?: string | null;
  gsmsHndlgType?: string;
  isEditMode?: boolean;
  onFieldChange?: (field: string, value: any) => void;
  onFieldBlur?: (field: string, event?: any) => void;
  onSetActivePackSizeTab?: (tabIndex: number) => void;
}

// Field type hints: currency = dollar format, percent = append %, str = raw string, int = integer
type ColType = 'currency' | 'percent' | 'str' | 'int' | 'num';

const MAS_DIRECT_COLUMNS: Array<{ key: string; label: string; type?: ColType }> = [
  { key: 'conceptName', label: 'DESCRIPTION', type: 'str' },
  { key: 'mfgBottleSize', label: 'MFG BOTTLE SIZE', type: 'int' },
  { key: 'supplierBulkNdc', label: 'Direct Only Supplier Bulk NDC#', type: 'str' },
  { key: 'gsms_mfg_ndc', label: 'GSMS NDC#', type: 'str' },
  { key: 'suggestedGsmsWac', label: 'SUPPLIER COMPARABLE WAC', type: 'currency' },
  { key: 'wac', label: 'WAC', type: 'currency' },
  { key: 'gsmsCostFromMfg', label: 'GSMS COST FROM MFG', type: 'currency' },
  { key: 'gsmsNetCostFromMfg', label: 'GSMS NET COST FROM MFG', type: 'currency' },
  { key: 'gsmsPackSize', label: 'GSMS PACK SIZE', type: 'int' },
  { key: 'gsmsHndlgType', label: 'GSMS HNDLG TYPE', type: 'str' },
  { key: 'gsmsBottleCapacity', label: 'GSMS BOTTLE CAPACITY', type: 'str' },
  { key: 'gsmsBottleCost', label: 'GSMS BOTTLE COST', type: 'currency' },
  { key: 'gsmsCapCost', label: 'GSMS CAP COST', type: 'currency' },
  { key: 'gsmsLabelCost', label: 'GSMS LABEL COST', type: 'currency' },
  { key: 'gsmsInsertCost', label: 'GSMS INSERT COST', type: 'currency' },
  { key: 'gsmsIffFee', label: 'GSMS IFF FEE', type: 'currency' },
  { key: 'gsmsBoxCartonCost', label: 'GSMS BOX/CARTON COST', type: 'currency' },
  { key: 'gsmsDesiccantCost', label: 'GSMS DESICCANT COST', type: 'currency' },
  { key: 'outsourceRepackFee', label: 'OUTSOURCE REPACK FEE', type: 'currency' },
  { key: 'blendedWhlsFee', label: 'Blended WHLS FEE', type: 'currency' },
  { key: 'gsmsFrtCost', label: 'GSMS FRT. COST', type: 'currency' },
  { key: 'pv2Percent', label: 'GSMS PV 2%', type: 'currency' },
  { key: 'gsmsLabor', label: 'GSMS LABOR', type: 'currency' },
  { key: 'gsmsCost', label: 'GSMS COST', type: 'currency' },
  { key: 'suggestedBidPrice', label: 'SUGGESTED BID PRICE', type: 'currency' },
  { key: 'approvedBy', label: 'APPROVED BY', type: 'str' },
  { key: 'gsmsProfit', label: 'GSMS PROFIT', type: 'percent' },
  { key: 'gsmsMarketingFee', label: 'GSMS MARKETING FEE', type: 'currency' },
  { key: 'vaAnnualUsage', label: 'VA ANNUAL USAGE', type: 'num' },
  { key: 'dodAnnualUsage', label: 'DOD ANNUAL USAGE', type: 'num' },
  { key: 'vaTmsPercent', label: 'VA TMS %', type: 'percent' },
  { key: 'dodTmsPercent', label: 'DoD TMS %', type: 'percent' },
  { key: 'totalTmsPercent', label: 'Total TMS %', type: 'percent' },
  { key: 'targetAnnualUsage', label: 'TARGET ANNUAL USAGE', type: 'num' },
  { key: 'contractAnnualRevenue', label: 'CONTRACT ANNUAL REVENUE', type: 'currency' },
  { key: 'contractAnnualGP', label: 'CONTRACT ANNUAL GP', type: 'currency' },
  { key: 'contractAnnualCost', label: 'CONTRACT ANNUAL COST', type: 'currency' },
  { key: 'govSavingsAnalysis', label: 'GOVT SAVINGS ANALYSIS', type: 'currency' },
  { key: 'tmopPercent', label: 'TMOP %', type: 'percent' },
  { key: 'annualTmopSavings', label: 'ANNUAL TMOP SAVINGS', type: 'currency' },
  { key: 'monthlyTmopSavings', label: 'MONTHLY TMOP SAVINGS', type: 'currency' },
];

// Columns editable in MAS Direct edit mode
const MAS_DIRECT_EDITABLE_COLS = new Set([
  'mfgBottleSize',
  'suggestedGsmsWac',
  'wac',
  'gsmsCostFromMfg',
  'gsmsDesiccantCost',
  'suggestedBidPrice',
  'gsmsProfit',
  'vaAnnualUsage',
  'dodAnnualUsage',
  'vaTmsPercent',
  'dodTmsPercent',
  'tmopPercent',
]);

// Columns that should never wrap (description + all NDC columns)
const NOWRAP_COLS = new Set(['conceptName', 'supplierBulkNdc', 'gsms_mfg_ndc', 'gsms_ndc']);

// Map column key → handler field name (only for keys that differ from field name)
const COL_KEY_TO_HANDLER_FIELD: Record<string, string> = {
  suggestedGsmsWac: 'supplier_comparable_wac',
};

export const MASDirectGlobalOutputTable: React.FC<MASDirectGlobalOutputTableProps> = ({
  formData,
  packSizeFormData,
  activePackSizeTab,
  bidApprovedBy,
  gsmsHndlgType,
  isEditMode,
  onFieldChange,
  onFieldBlur,
  onSetActivePackSizeTab,
}) => {
  const formatCurrency = (value: any): string => {
    if (value === null || value === undefined || value === '') return '$0.00';
    const num = typeof value === 'number' ? value : parseFloat(String(value));
    if (isNaN(num)) return '$0.00';
    return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatNum = (value: any): string => {
    if (value === null || value === undefined || value === '') return '0.00';
    const num = typeof value === 'number' ? value : parseFloat(String(value));
    if (isNaN(num)) return '0.00';
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatInt = (value: any): string => {
    if (value === null || value === undefined || value === '') return '—';
    const num = typeof value === 'number' ? value : parseFloat(String(value));
    if (isNaN(num)) return String(value) || '—';
    return String(Math.round(num));
  };

  const formatPercent = (value: any): string => {
    if (value === null || value === undefined || value === '') return '0.00%';
    const num = typeof value === 'number' ? value : parseFloat(String(value));
    if (isNaN(num)) return '0.00%';
    return `${num.toFixed(2)}%`;
  };

  const formatStr = (value: any): string => {
    if (value === null || value === undefined || value === '') return '—';
    return String(value);
  };

  const formatWac = (packData: any): string => {
    const wac = packData.wac ?? packData.supplier_wac;
    if (wac === null || wac === undefined || wac === '') return '—';
    const num = typeof wac === 'number' ? wac : parseFloat(String(wac));
    if (isNaN(num)) return '—';
    return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getCellValue = (
    packData: any,
    col: { key: string; type?: ColType },
    hndlgType?: string
  ): string => {
    // Special cases
    if (col.key === 'conceptName') return formatStr(packData.conceptName);
    if (col.key === 'wac') return formatWac(packData);
    if (col.key === 'supplierBulkNdc')
      return formatStr(packData.selectedMfgNdc || packData.mfg_ndc);
    if (col.key === 'gsms_mfg_ndc') return formatStr(packData.gsms_ndc ?? '');
    if (col.key === 'gsms_ndc') return formatStr(packData.gsms_ndc);
    if (col.key === 'mfgBottleSize') return formatInt(packData.mfgBottleSize);
    if (col.key === 'gsmsPackSize') return formatInt(packData.gsmsPackSize ?? packData.packSize);
    if (col.key === 'gsmsHndlgType') return formatStr(hndlgType || packData.gsmsHndlgType);
    if (col.key === 'gsmsBottleCapacity') return formatStr(packData.gsmsBottleCapacity);
    if (col.key === 'approvedBy') {
      const value = bidApprovedBy ?? packData.approvedBy;
      return value == null || value === '' ? '—' : String(value);
    }
    if (col.key === 'suggestedGsmsWac')
      return formatCurrency(packData.suggestedGsmsWac ?? packData.supplier_comparable_wac);
    if (col.key === 'govSavingsAnalysis') return formatCurrency(packData.govSavingsAnalysis);
    if (col.key === 'annualTmopSavings') return formatCurrency(packData.annualTmopSavings);
    if (col.key === 'monthlyTmopSavings') return formatCurrency(packData.monthlyTmopSavings);

    const raw = packData[col.key];
    switch (col.type) {
      case 'currency':
        return formatCurrency(raw);
      case 'percent':
        return formatPercent(raw);
      case 'int':
        return formatInt(raw);
      case 'str':
        return formatStr(raw);
      case 'num':
      default:
        return formatNum(raw);
    }
  };

  const getHandlerField = (colKey: string): string => COL_KEY_TO_HANDLER_FIELD[colKey] ?? colKey;

  const getRawValue = (packData: any, col: { key: string; type?: ColType }): number | null => {
    let v: any;
    if (col.key === 'suggestedGsmsWac')
      v = packData.suggestedGsmsWac ?? packData.supplier_comparable_wac;
    else if (col.key === 'wac') v = packData.wac ?? packData.supplier_wac;
    else if (col.key === 'mfgBottleSize') v = packData.mfgBottleSize;
    else v = packData[col.key];
    if (v === null || v === undefined || v === '') return null;
    const n = parseFloat(String(v));
    return isNaN(n) ? null : n;
  };

  // Extract global Project Information fields from formData to apply to every row
  const globalFields = useMemo(() => {
    const GLOBAL_KEYS = [
      'gsmsHndlgType',
      'supplierName',
      'analysisDate',
      'targetLaunchDate',
      'analysisType',
      'contracts',
      'contractsData',
      'productName',
      'analystName',
    ];
    const result: Record<string, any> = {};
    if (formData) {
      GLOBAL_KEYS.forEach(k => {
        if (formData[k] !== undefined && formData[k] !== null && formData[k] !== '') {
          result[k] = formData[k];
        }
      });
    }
    return result;
  }, [formData]);

  const allPackSizes = useMemo(() => {
    let data: any[] = [];
    if (packSizeFormData && Object.keys(packSizeFormData).length > 0) {
      const entries = Object.entries(packSizeFormData);
      data = entries
        .map(([key, packData]: [string, any]) => {
          if (!packData || (!packData.conceptName && !packData.packSize)) return null;
          const packKey = Number(key);
          const isActivePack =
            activePackSizeTab != null && packKey === activePackSizeTab && formData;
          // globalFields applied as base so Project Info is visible for all rows
          const merged = isActivePack
            ? { ...globalFields, ...packData, ...formData }
            : { ...globalFields, ...packData };
          return { ...merged, _tabKey: packKey };
        })
        .filter(Boolean) as any[];
    } else if (formData && (formData.conceptName || formData.packSize)) {
      data = [{ ...formData, _tabKey: activePackSizeTab ?? 0 }];
    }
    return data.sort((a: any, b: any) => {
      const conceptA = (a.conceptName || '').toLowerCase();
      const conceptB = (b.conceptName || '').toLowerCase();
      if (conceptA !== conceptB) return conceptA.localeCompare(conceptB);
      const packSizeA = parseFloat(a.mfgBottleSize || a.packSize || a.gsmsPackSize || '0');
      const packSizeB = parseFloat(b.mfgBottleSize || b.packSize || b.gsmsPackSize || '0');
      return packSizeA - packSizeB;
    });
  }, [packSizeFormData, formData, globalFields, activePackSizeTab]);

  const totals = useMemo(() => {
    let totalRevenue = 0;
    let totalGP = 0;
    let totalCost = 0;
    let totalSavings = 0;
    let totalNcEstAnnualUsage = 0;
    let totalTargetAnnualUsage = 0;
    let totalAnnualTmopSavings = 0;
    let totalMonthlyTmopSavings = 0;
    allPackSizes.forEach((packData: any) => {
      const revenue = parseFloat(packData.contractAnnualRevenue || '0');
      const gp = parseFloat(packData.contractAnnualGP || '0');
      const cost = parseFloat(packData.contractAnnualCost || '0');
      const savings = parseFloat(packData.govSavingsAnalysis || '0');
      const ncEstAnnualUsage = parseFloat(packData.ncEstAnnualUsage || '0');
      const targetAnnualUsage = parseFloat(packData.targetAnnualUsage || '0');
      const annualTmop = parseFloat(packData.annualTmopSavings || '0');
      const monthlyTmop = parseFloat(packData.monthlyTmopSavings || '0');
      if (!isNaN(revenue)) totalRevenue += revenue;
      if (!isNaN(gp)) totalGP += gp;
      if (!isNaN(cost)) totalCost += cost;
      if (!isNaN(savings)) totalSavings += savings;
      if (!isNaN(ncEstAnnualUsage)) totalNcEstAnnualUsage += ncEstAnnualUsage;
      if (!isNaN(targetAnnualUsage)) totalTargetAnnualUsage += targetAnnualUsage;
      if (!isNaN(annualTmop)) totalAnnualTmopSavings += annualTmop;
      if (!isNaN(monthlyTmop)) totalMonthlyTmopSavings += monthlyTmop;
    });
    return {
      revenue: totalRevenue,
      gp: totalGP,
      cost: totalCost,
      savings: totalSavings,
      ncEstAnnualUsage: totalNcEstAnnualUsage,
      targetAnnualUsage: totalTargetAnnualUsage,
      annualTmopSavings: totalAnnualTmopSavings,
      monthlyTmopSavings: totalMonthlyTmopSavings,
    };
  }, [allPackSizes]);

  if (allPackSizes.length === 0) {
    return (
      <div className={classes.globalTableWrapper}>
        <p>No data available</p>
      </div>
    );
  }

  const totalSpan = MAS_DIRECT_COLUMNS.length - 8;

  return (
    <div className={classes.globalTableWrapper}>
      <table className={classes.globalTable}>
        <thead>
          <tr>
            {MAS_DIRECT_COLUMNS.map(c => (
              <th
                key={c.key}
                style={
                  c.key === 'conceptName'
                    ? { minWidth: '180px', whiteSpace: 'nowrap' }
                    : NOWRAP_COLS.has(c.key)
                      ? { whiteSpace: 'nowrap', minWidth: '100px' }
                      : {
                          whiteSpace: 'normal',
                          wordBreak: 'keep-all',
                          overflowWrap: 'normal',
                          minWidth: '90px',
                          maxWidth: '130px',
                        }
                }
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {allPackSizes.map((packData: any, index: number) => {
            return (
              <tr key={index}>
                {MAS_DIRECT_COLUMNS.map(c => {
                  const canEdit =
                    isEditMode &&
                    MAS_DIRECT_EDITABLE_COLS.has(c.key) &&
                    !!onFieldChange &&
                    !!onFieldBlur;
                  if (canEdit) {
                    const fieldName = getHandlerField(c.key);
                    const rawVal = getRawValue(packData, c);
                    return (
                      <td
                        key={c.key}
                        style={{
                          padding: '4px',
                          width: '100px',
                          minWidth: '80px',
                          verticalAlign: 'middle',
                          textAlign: 'center',
                        }}
                      >
                        <InputNumber
                          value={rawVal}
                          onValueChange={e => onFieldChange!(fieldName, e.value)}
                          onFocus={() => {
                            if (packData._tabKey !== activePackSizeTab && onSetActivePackSizeTab) {
                              onSetActivePackSizeTab(packData._tabKey);
                            }
                          }}
                          onBlur={e => {
                            const target = e.target as HTMLElement;
                            const inputEl =
                              target.tagName === 'INPUT'
                                ? (target as HTMLInputElement)
                                : target.querySelector<HTMLInputElement>('input');
                            if (inputEl) {
                              const cleaned = inputEl.value.replace(/[$,\s%]/g, '');
                              const num = parseFloat(cleaned);
                              if (!isNaN(num)) {
                                onFieldBlur!(fieldName, num);
                                return;
                              }
                            }
                            onFieldBlur!(fieldName, e);
                          }}
                          onKeyDown={e => {
                            if (e.key !== 'Tab') return;
                            const target = e.target as HTMLElement;
                            const inputEl =
                              target.tagName === 'INPUT'
                                ? (target as HTMLInputElement)
                                : target.querySelector<HTMLInputElement>('input');
                            if (inputEl) {
                              const cleaned = inputEl.value.replace(/[$,\s%]/g, '');
                              const num = parseFloat(cleaned);
                              if (!isNaN(num)) onFieldBlur!(fieldName, num);
                            }
                          }}
                          mode="decimal"
                          minFractionDigits={c.type === 'int' ? 0 : 2}
                          maxFractionDigits={c.type === 'int' ? 0 : 2}
                          style={{ width: '100px' }}
                          inputStyle={{ fontSize: '0.75rem', padding: '4px 6px', width: '100%' }}
                        />
                      </td>
                    );
                  }
                  return (
                    <td
                      key={c.key}
                      className={c.key === 'gsms_mfg_ndc' ? classes.greenText : undefined}
                      style={NOWRAP_COLS.has(c.key) ? { whiteSpace: 'nowrap' } : undefined}
                    >
                      {getCellValue(packData, c, gsmsHndlgType)}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={totalSpan}>
              <strong>Total</strong>
            </td>
            <td>
              <strong>{formatNum(totals.targetAnnualUsage)}</strong>
            </td>
            <td>
              <strong>{formatCurrency(totals.revenue)}</strong>
            </td>
            <td>
              <strong>{formatCurrency(totals.gp)}</strong>
            </td>
            <td>
              <strong>{formatCurrency(totals.cost)}</strong>
            </td>
            <td>
              <strong>{formatCurrency(totals.savings)}</strong>
            </td>
            <td>
              <strong>—</strong>
            </td>
            <td>
              <strong>{formatCurrency(totals.annualTmopSavings)}</strong>
            </td>
            <td>
              <strong>{formatCurrency(totals.monthlyTmopSavings)}</strong>
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};
